import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useLayoutEffect, useState, type RefObject } from 'react';
import { useDensity } from '../../preferences/preferences.store.ts';
import { ROW_HEIGHTS } from '../../tokens/density.ts';

export const VIRTUAL_FROM_ROWS = 200;

const OVERSCAN_ROWS = 12;

export type VirtualRows = {
  readonly enabled: boolean;
  readonly first: number;
  readonly last: number;
  readonly paddingTop: number;
  readonly paddingBottom: number;
  readonly measureRow: (row: HTMLTableRowElement | null) => void;
  readonly scrollTo: (index: number) => void;
};

type VirtualRowsInput = {
  readonly count: number;
  readonly bodyRef: RefObject<HTMLTableSectionElement | null>;
  readonly enabled: boolean;
};

const ignoreRow = (): void => undefined;

export const useVirtualRows = ({ count, bodyRef, enabled }: VirtualRowsInput): VirtualRows => {
  const rowHeight = ROW_HEIGHTS[useDensity()];
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    if (!enabled) return;
    setScrollMargin((bodyRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY);
  }, [enabled, bodyRef]);
  const virtualizer = useWindowVirtualizer({
    count: enabled ? count : 0,
    estimateSize: () => rowHeight,
    measureElement: (element) => element.getBoundingClientRect().height || rowHeight,
    overscan: OVERSCAN_ROWS,
    scrollMargin,
    enabled,
  });
  if (!enabled)
    return {
      enabled,
      first: 0,
      last: count,
      paddingTop: 0,
      paddingBottom: 0,
      measureRow: ignoreRow,
      scrollTo: ignoreRow,
    };
  const items = virtualizer.getVirtualItems();
  const firstItem = items[0];
  const lastItem = items.at(-1);
  return {
    enabled,
    first: firstItem?.index ?? 0,
    last: lastItem === undefined ? 0 : lastItem.index + 1,
    paddingTop: firstItem === undefined ? 0 : firstItem.start - scrollMargin,
    paddingBottom:
      lastItem === undefined ? 0 : Math.max(0, virtualizer.getTotalSize() - (lastItem.end - scrollMargin)),
    measureRow: (row) => {
      virtualizer.measureElement(row);
    },
    scrollTo: (index) => {
      virtualizer.scrollToIndex(index, { align: 'auto' });
    },
  };
};
